import { environment } from '../../../environments/environment';
import {
  hostedAppUrlsForHostname,
  hostedGatewayOriginsForHostname,
} from '../../../environments/gateway-origins';
import { eventqOktaCallbackRoute, eventqOktaRedirectUriFromEnv } from './okta-redirect';

const BOOT_PREFIX = '[EventQ:boot]';
const OKTA_PREFIX = '[EventQ:Okta]';
const STS_PREFIX = '[EventQ:STS]';
const PIPELINE_TOKEN = /^#\{PROD_[A-Z0-9_]+\}#$/;

/**
 * Always-on diagnostics for every build (mock / dev / production alike).
 * These are deliberately not gated on `environment.production`: staging is built
 * from environment.production.ts, so `!production` logs never fired there.
 *
 * Every entry point swallows its own failures — a diagnostic must never break
 * login, and a broken payload must still leave a line in the console.
 */
export function logEventqEnvironmentOnBoot(): void {
  emit(BOOT_PREFIX, 'app first load — environment.ts for this build', () =>
    cloneEnvironmentForLog(),
  );
  emit(BOOT_PREFIX, 'derived Okta + browser', () => ({
    derivedOkta: derivedOktaSnapshot(),
    browser: browserSnapshot(),
  }));
  emit(BOOT_PREFIX, 'landing URL vs Okta redirectUri', () => landingUrlForLog());
}

export function logOktaDebug(step: string, extra?: Record<string, unknown>): void {
  emit(OKTA_PREFIX, step, () => ({
    at: new Date().toISOString(),
    ...extra,
    env: cloneEnvironmentForLog(),
    derivedOkta: derivedOktaSnapshot(),
    browser: browserSnapshot(),
  }));
}

export function logStsDebug(step: string, extra?: Record<string, unknown>): void {
  emit(STS_PREFIX, step, () => {
    const hosted = hostedGatewayOriginsForHostname(globalThis.location?.hostname ?? '');
    return {
      at: new Date().toISOString(),
      ...extra,
      stsEnv: { ...environment.sts },
      apiOrigin: hosted?.apiOrigin ?? environment.apiOrigin,
      authOrigin: hosted?.authOrigin ?? environment.authOrigin,
      apiMode: environment.apiMode,
      production: environment.production,
      browser: browserSnapshot(),
    };
  });
}

/** Logs the step even when building its payload throws. */
function emit(prefix: string, step: string, payload: () => unknown): void {
  let built: unknown;
  try {
    built = payload();
  } catch (error: unknown) {
    built = { payloadFailed: String(error) };
  }
  try {
    console.warn(prefix, step, built);
  } catch {
    // A console that rejects the payload must not break the auth flow.
  }
}

export function serializeUnknownError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    const extra = error as Error & Record<string, unknown>;
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      errorCode: extra['errorCode'],
      errorSummary: extra['errorSummary'],
      xhrStatus: extra['xhr'] && typeof extra['xhr'] === 'object'
        ? (extra['xhr'] as { status?: number }).status
        : undefined,
    };
  }
  if (error && typeof error === 'object') {
    try {
      return { json: JSON.parse(JSON.stringify(error)) as unknown };
    } catch {
      return { string: String(error) };
    }
  }
  return { value: String(error) };
}

export function cloneEnvironmentForLog(): Record<string, unknown> {
  const cloned = cloneEnvironment();
  cloned['doriApiToken'] = describeSecretishField(environment.doriApiToken);
  const hostname = globalThis.location?.hostname ?? '';
  const origin = globalThis.location?.origin ?? '';
  const hosted = hostedGatewayOriginsForHostname(hostname);
  if (hosted) {
    cloned['apiOrigin'] = hosted.apiOrigin;
    cloned['authOrigin'] = hosted.authOrigin;
  }
  const appUrls = hostedAppUrlsForHostname(hostname, origin);
  if (appUrls) {
    cloned['eventqAppBaseUrl'] = appUrls.eventqAppBaseUrl;
    cloned['resolveAppBaseUrl'] = appUrls.resolveAppBaseUrl;
    cloned['resolveUrl'] = appUrls.resolveUrl;
  }
  return cloned;
}

/** `structuredClone` is missing on older browsers; JSON round-trip is enough for plain config. */
function cloneEnvironment(): Record<string, unknown> {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(environment) as unknown as Record<string, unknown>;
    } catch {
      // fall through to JSON
    }
  }
  return JSON.parse(JSON.stringify(environment)) as Record<string, unknown>;
}

const OAUTH_QUERY_REDACT_KEYS = new Set([
  'code',
  'state',
  'id_token',
  'access_token',
  'session_state',
  'error_description',
]);

/**
 * Where the browser actually landed vs the redirect URI this build sends to Okta.
 * OAuth `code` / `state` are redacted — expand this line after an Okta return.
 */
export function landingUrlForLog(): Record<string, unknown> {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return { available: false };
  }

  const loc = window.location;
  const pathname = normalizePath(loc.pathname ?? '');
  const expectedRedirectUri = eventqOktaRedirectUriFromEnv();
  const expectedCallbackPath = normalizePath(`/${eventqOktaCallbackRoute()}`);
  const onExpectedCallback = pathname === expectedCallbackPath;
  const onHostedSsoLogin = pathname === '/sso/login';
  const onLoginPage = pathname === '/login';
  const hasOAuthCallbackQuery = hasOAuthCallbackParams(loc.search, loc.hash);

  return {
    available: true,
    hrefRedacted: redactHref(loc.href ?? ''),
    pathname,
    searchRedacted: redactSearch(loc.search ?? ''),
    hashRedacted: redactHash(loc.hash ?? ''),
    expectedRedirectUri,
    expectedCallbackPath,
    onExpectedCallback,
    onHostedSsoLogin,
    onLoginPage,
    hasOAuthCallbackQuery,
    diagnosis: diagnoseLanding({
      onExpectedCallback,
      onHostedSsoLogin,
      onLoginPage,
      hasOAuthCallbackQuery,
      expectedCallbackPath,
    }),
  };
}

function diagnoseLanding(flags: {
  onExpectedCallback: boolean;
  onHostedSsoLogin: boolean;
  onLoginPage: boolean;
  hasOAuthCallbackQuery: boolean;
  expectedCallbackPath: string;
}): string {
  if (flags.onExpectedCallback) {
    return 'Landed on the EventQ Okta callback route — LoginOktaCallback should run.';
  }
  if (flags.onHostedSsoLogin) {
    return 'Landed on /sso/login (hosted callback). Local wildcard route will send you to /login and drop the code.';
  }
  if (flags.onLoginPage && flags.hasOAuthCallbackQuery) {
    return 'Landed on /login with OAuth query params — Okta redirect URI is probably /login, not the callback.';
  }
  if (flags.onLoginPage) {
    return 'Landed on /login with no callback query — Okta did not return to the expected callback path.';
  }
  if (flags.hasOAuthCallbackQuery) {
    return `OAuth query on an unexpected path — expected ${flags.expectedCallbackPath}.`;
  }
  return `Landed on an unexpected path — compare pathname to ${flags.expectedCallbackPath}.`;
}

function normalizePath(pathname: string): string {
  const trimmed = pathname.trim() || '/';
  if (trimmed === '/') {
    return '/';
  }
  return trimmed.replace(/\/+$/, '');
}

function hasOAuthCallbackParams(search: string, hash: string): boolean {
  const combined = `${search}\n${hash}`;
  return /(?:^|[?#&])(code|state|id_token|access_token)=/.test(combined);
}

function redactSearch(search: string): string {
  if (!search) {
    return '';
  }
  const prefix = search.startsWith('?') ? '?' : '';
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const parts: string[] = [];
  for (const [key, value] of params.entries()) {
    if (OAUTH_QUERY_REDACT_KEYS.has(key.toLowerCase()) && value) {
      parts.push(`${key}=[redacted]`);
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.length ? `${prefix}${parts.join('&')}` : prefix;
}

function redactHash(hash: string): string {
  if (!hash) {
    return '';
  }
  if (!/(?:code|id_token|access_token|state)=/.test(hash)) {
    return hash;
  }
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw.includes('=')) {
    return '[redacted hash]';
  }
  return `#${redactSearch(`?${raw}`).slice(1)}`;
}

function redactHref(href: string): string {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}${redactSearch(url.search)}${redactHash(url.hash)}`;
  } catch {
    return href.replace(/(code|state|id_token|access_token|session_state)=[^&#]*/gi, '$1=[redacted]');
  }
}

export function derivedOktaSnapshot(): Record<string, unknown> {
  const issuer = environment.okta.issuer ?? '';
  const clientId = environment.okta.clientId ?? '';
  const callbackPath = environment.okta.callbackPath ?? '';
  const appBase = environment.eventqAppBaseUrl ?? '';
  const redirectUri = eventqOktaRedirectUriFromEnv();
  const callbackRoute = eventqOktaCallbackRoute();

  return {
    issuerUsable: isUsableConfigValue(issuer),
    clientIdUsable: isUsableConfigValue(clientId),
    callbackPathUsable: isUsableConfigValue(callbackPath),
    eventqAppBaseUrlUsable: isUsableConfigValue(appBase),
    redirectUri,
    redirectUriEmpty: redirectUri.length === 0,
    callbackRoute,
    wouldBeConfigured: Boolean(
      issuer.trim() && clientId.trim() && redirectUri && isUsableConfigValue(issuer) && isUsableConfigValue(clientId),
    ),
    placeholders: {
      issuer: PIPELINE_TOKEN.test(issuer.trim()),
      clientId: PIPELINE_TOKEN.test(clientId.trim()),
      callbackPath: PIPELINE_TOKEN.test(callbackPath.trim()),
      eventqAppBaseUrl: PIPELINE_TOKEN.test(appBase.trim()),
    },
  };
}

function isUsableConfigValue(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && !PIPELINE_TOKEN.test(trimmed);
}

function describeSecretishField(value: string | undefined): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return '';
  }
  if (PIPELINE_TOKEN.test(trimmed)) {
    return trimmed;
  }
  return `[redacted length=${trimmed.length}]`;
}

function browserSnapshot(): Record<string, unknown> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { available: false };
  }

  // Reading window.localStorage itself throws SecurityError when storage is blocked.
  const oktaStorageKeys = (read: () => Storage): string[] => {
    try {
      const storage = read();
      return Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
        (key): key is string => Boolean(key) && /okta/i.test(key as string),
      );
    } catch {
      return ['<unavailable>'];
    }
  };

  return {
    available: true,
    href: window.location.href,
    origin: window.location.origin,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    baseURI: document.baseURI,
    documentBaseHref: document.querySelector('base')?.getAttribute('href') ?? null,
    oktaStorageKeys: {
      local: oktaStorageKeys(() => window.localStorage),
      session: oktaStorageKeys(() => window.sessionStorage),
    },
  };
}
